import { useNavigate } from 'react-router-dom';
import { Newspaper, ArrowLeft, Shield, Mail, Eye, Database, Clock, UserX, Building2, MessageSquare } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

function PrivacyPolicy() {
    const navigate = useNavigate();

    const sections = [
        {
            icon: <Eye className="w-5 h-5" />,
            title: "What We Collect",
            content: [
                "Your email address — when you subscribe to our newsletter",
                "Basic usage data — pages visited and general interaction patterns via Google Analytics",
                "We do NOT collect names, phone numbers, addresses, or payment information"
            ]
        },
        {
            icon: <Mail className="w-5 h-5" />,
            title: "How We Use Your Data",
            content: [
                "To send you our daily AI newsletter with curated news and insights",
                "To deliver daily AI intelligence briefs directly to your inbox",
                "To analyze site traffic and improve our content using anonymized analytics"
            ]
        },
        {
            icon: <Building2 className="w-5 h-5" />,
            title: "Third-Party Services",
            content: [
                "Resend (resend.com) — delivers our newsletter emails on our behalf",
                "Google Analytics (analytics.google.com) — helps us understand site traffic",
                "PostHog (posthog.com) — product analytics and session recording",
                "These providers process your data under their own privacy policies and GDPR-compliant agreements"
            ]
        },
        {
            icon: <Database className="w-5 h-5" />,
            title: "Data Storage & Security",
            content: [
                "Your email is stored securely in our database on an encrypted server",
                "We do not sell, rent, or share your email with any third parties for marketing purposes",
                "Access to subscriber data is restricted to authorized personnel only"
            ]
        },
        {
            icon: <Clock className="w-5 h-5" />,
            title: "Data Retention",
            content: [
                "We retain your email for as long as your subscription is active",
                "If you unsubscribe, your email is marked inactive but retained for audit purposes",
                "You may request full deletion of your data at any time (see below)"
            ]
        },
        {
            icon: <UserX className="w-5 h-5" />,
            title: "Your Rights (GDPR)",
            content: [
                "Access — request a copy of all data we hold about you",
                "Rectification — ask us to correct any inaccurate data",
                "Deletion — request that we permanently delete your personal data",
                "Withdrawal — unsubscribe at any time via the link in every email",
                "Complaint — lodge a complaint with your local data protection authority"
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="max-w-2xl w-full">
                {/* Header */}
                <div className="text-center mb-8">
                    <img
                        src="/logo192.png"
                        alt="TNF"
                        className="w-16 h-16 rounded-xl object-cover border border-emerald-500/25 shadow-md mx-auto mb-4"
                    />
                    <h1 className="text-2xl font-black tracking-tight mb-1">THE NEURAL FEED</h1>
                    <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                        AI Intelligence • Daily Insights
                    </p>
                </div>

                {/* Privacy Policy Card */}
                <Card className="border-2">
                    <CardContent className="p-8">
                        <div className="flex items-center gap-3 mb-2">
                            <Shield className="w-6 h-6 text-primary" />
                            <h2 className="text-xl font-bold">Privacy Policy</h2>
                        </div>
                        <p className="text-sm text-muted-foreground mb-6">
                            Last updated: April 18, 2026
                        </p>

                        <p className="text-sm text-foreground mb-6">
                            The Neural Feed ("we", "us", "our") operates the website theneuralfeed.com.
                            This page informs you of our policies regarding the collection, use, and disclosure
                            of personal data when you use our Service.
                        </p>

                        <Separator className="mb-6" />

                        {/* Sections */}
                        <div className="space-y-6">
                            {sections.map((section, idx) => (
                                <div key={idx}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-primary">{section.icon}</span>
                                        <h3 className="text-sm font-bold uppercase tracking-wide">{section.title}</h3>
                                    </div>
                                    <ul className="space-y-1 ml-7">
                                        {section.content.map((item, i) => (
                                            <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                                <span className="text-primary mt-1.5 shrink-0">•</span>
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>

                        <Separator className="my-6" />

                        {/* Contact */}
                        <div className="flex items-start gap-2">
                            <MessageSquare className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                            <div>
                                <h3 className="text-sm font-bold uppercase tracking-wide mb-1">Contact Us</h3>
                                <p className="text-sm text-muted-foreground">
                                    For any privacy-related questions, data access requests, or deletion requests, contact us at:{' '}
                                    <a
                                        href="mailto:contact@theneuralfeed.com"
                                        className="text-primary hover:underline"
                                    >
                                        contact@theneuralfeed.com
                                    </a>
                                </p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    We will respond to all requests within 30 days as required by GDPR.
                                </p>
                            </div>
                        </div>

                        <Separator className="my-6" />

                        {/* Back button */}
                        <Button
                            onClick={() => navigate('/')}
                            className="w-full rounded-full"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to The Neural Feed
                        </Button>
                    </CardContent>
                </Card>

                {/* Footer */}
                <p className="text-center text-xs text-muted-foreground mt-6">
                    172+ AI Sources • Daily Updates • GDPR Compliant
                </p>
            </div>
        </div>
    );
}

export default PrivacyPolicy;
